import React from 'react';
import { DEFAULT_PANES } from '../../../constants/windowDefaults';

/**
 * Divided-light grille for the window schematics. Shared by the flat
 * window front view (`WindowElevationSchematic`) and the bay window's
 * centre unit (`BayWindowSchematic`), which draw in different units —
 * the grille only ever works inside the `sash` region it is handed.
 *
 * Three patterns:
 *   none      — no grilles
 *   colonial  — full panes.x × panes.y grid
 *   prairie   — perimeter band only (one row/col along each edge)
 *
 * It fills exactly the region it is given. Which regions a window has, and
 * which of them are grilled (a hung window's `upperGrilleOnly` skips the lower
 * sash), is decided by `glazingGrid` in the schematic.
 */
export function GrilleOverlay({
  sash,
  panes,
  grillePattern,
  strokeColor,
  strokeWidth,
}) {
  if (grillePattern === 'none') return null;
  // A pane COUNT is what a colonial grid is made of, so 1×1 means no grid.
  // Prairie does not use `panes` at all — it is a band inset from the edges —
  // and the shared guard was silently swallowing it: `WindowPanel.setGrille`
  // only writes `panes` for 'colonial' and 'none', so picking Prairie after
  // None left the count at 1×1 and nothing was drawn.
  if (grillePattern !== 'prairie' && panes.x <= 1 && panes.y <= 1) {
    return null;
  }
  // The caller hands us ONE glazed area and we fill it. Which areas exist —
  // and which of them get a grille at all — is the caller's decision
  // (`glazingGrid`), so a hung window's "upper sash only" is a region the
  // caller declines to draw rather than a half-height clamp in here. The clamp
  // produced a different window: half-height lights inside a full-height sash.
  const region = sash;

  const lines = [];
  if (grillePattern === 'prairie') {
    const inset = Math.min(region.w, region.h) * 0.12;
    const innerX1 = region.x + inset;
    const innerX2 = region.x + region.w - inset;
    const innerY1 = region.y + inset;
    const innerY2 = region.y + region.h - inset;
    lines.push(
      <line
        key="p-l"
        x1={innerX1}
        y1={region.y}
        x2={innerX1}
        y2={region.y + region.h}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />,
      <line
        key="p-r"
        x1={innerX2}
        y1={region.y}
        x2={innerX2}
        y2={region.y + region.h}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />,
      <line
        key="p-t"
        x1={region.x}
        y1={innerY1}
        x2={region.x + region.w}
        y2={innerY1}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />,
      <line
        key="p-b"
        x1={region.x}
        y1={innerY2}
        x2={region.x + region.w}
        y2={innerY2}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    );
    return <>{lines}</>;
  }
  // colonial: full grid
  for (let i = 1; i < panes.x; i += 1) {
    const x = region.x + (region.w * i) / panes.x;
    lines.push(
      <line
        key={`v-${i}`}
        x1={x}
        y1={region.y}
        x2={x}
        y2={region.y + region.h}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    );
  }
  for (let j = 1; j < panes.y; j += 1) {
    const y = region.y + (region.h * j) / panes.y;
    lines.push(
      <line
        key={`h-${j}`}
        x1={region.x}
        y1={y}
        x2={region.x + region.w}
        y2={y}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    );
  }
  return <>{lines}</>;
}

export function normalizePanes(panes) {
  // panes can be: undefined, {x, y}, or a legacy single number
  // (interpreted as total count → infer a balanced grid).
  if (panes && typeof panes === 'object') {
    return {
      x: Math.max(1, Number(panes.x) || 1),
      y: Math.max(1, Number(panes.y) || 1),
    };
  }
  if (typeof panes === 'number' && panes > 1) {
    const side = Math.round(Math.sqrt(panes));
    return { x: side, y: Math.max(1, Math.round(panes / side)) };
  }
  // Nothing stored: the SHARED default, not a local one. This used to return
  // 1x1 while 3D and the panel both used 2x2 — see DEFAULT_PANES.
  return { ...DEFAULT_PANES };
}
